import PropTypes from 'prop-types';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Form } from 'react-final-form';
import { Button } from '@unite-us/ui';
import fetchProvidersUserCore from 'src/actions/UserProvider/fetchProvidersUser';
import { fetchGroupsUsersFromProgram } from 'actions/User/Program/Group';
import { fetchServiceCase, updateServiceCase } from 'actions/Case/Contact/Group';
import { get, debounce } from 'lodash';
import { setDashboardRefetch } from 'actions/Dashboard';
import { primaryWorkerOptions } from 'src/components/Cases/utils/selectOptions';
import isCaseOON from 'src/components/Cases/utils/isCaseOON';
import PrimaryWorkerSelectField from './PrimaryWorkerSelectField';

const buildPrimaryWorkerOptions = (groupsUsers) => (
  primaryWorkerOptions({ groupsUsers }).map((option) => ({
    ...option,
    value: option.value == null ? '' : String(option.value),
    label: option.label,
  }))
);

const fetchUsersForCase = ({
  referOON,
  fetchProviders,
  fetchGroupsUsers,
  groupId,
  programId,
  search,
}) => {
  if (referOON) {
    fetchProviders({
      providers: groupId,
      options: search ? {
        text: search,
        page: 1,
        licensed: false,
      } : { licensed: false },
    });
    return;
  }

  if (search) {
    fetchGroupsUsers(groupId, programId, { text: search, per: 50, page: 1 });
    return;
  }

  fetchGroupsUsers(groupId, programId);
};

const submitPrimaryWorkerAssignment = async ({
  groupId,
  contactId,
  serviceCase,
  values,
  updateCase,
  fetchCase,
  setRefetch,
  closeModal,
}) => {
  const formInputs = {
    service_case: {
      primary_worker_id: values.primary_worker_id,
    },
  };

  await updateCase(groupId, contactId, serviceCase.id, formInputs);
  await fetchCase(groupId, contactId, serviceCase.id);

  setRefetch();
  closeModal();
};

export function ServiceCasePrimaryWorkerForm(props) {
  const {
    closeModal,
    contactId,
    groupId,
    groupsUsers,
    serviceCase,
    // Redux actions
    fetchProvidersUserCore: fetchProviders,
    fetchGroupsUsersFromProgram: fetchGroupsUsers,
    fetchServiceCase: fetchCase,
    updateServiceCase: updateCase,
    setDashboardRefetch: setRefetch,
  } = props;

  const [submitting, setSubmitting] = useState(false);

  const programId = get(serviceCase, 'program.id', null);
  const referOON = isCaseOON(serviceCase);
  const initialPrimaryWorkerId = get(serviceCase, 'primary_worker.id');

  useEffect(() => {
    fetchUsersForCase({
      referOON,
      fetchProviders,
      fetchGroupsUsers,
      groupId,
      programId,
    });
  }, [groupId, programId, referOON, fetchProviders, fetchGroupsUsers]);

  const debouncedFetchGroupsUsers = useMemo(
    () => debounce((search) => {
      fetchUsersForCase({
        referOON,
        fetchProviders,
        fetchGroupsUsers,
        groupId,
        programId,
        search,
      });
    }, 400),
    [groupId, programId, referOON, fetchProviders, fetchGroupsUsers],
  );

  useEffect(() => () => debouncedFetchGroupsUsers.cancel(), [debouncedFetchGroupsUsers]);

  const options = useMemo(
    () => buildPrimaryWorkerOptions(groupsUsers),
    [groupsUsers],
  );

  const handleSubmit = useCallback(async (values) => {
    setSubmitting(true);
    try {
      await submitPrimaryWorkerAssignment({
        groupId,
        contactId,
        serviceCase,
        values,
        updateCase,
        fetchCase,
        setRefetch,
        closeModal,
      });
    } finally {
      setSubmitting(false);
    }
  }, [groupId, contactId, serviceCase, updateCase, fetchCase, setRefetch, closeModal]);

  const initialValues = useMemo(() => ({
    primary_worker_id: initialPrimaryWorkerId != null ? String(initialPrimaryWorkerId) : null,
  }), [initialPrimaryWorkerId]);

  const handleInputChange = useCallback((inputValue, actionMeta) => {
    if (actionMeta?.action === 'input-change') {
      debouncedFetchGroupsUsers(inputValue);
    }
    return inputValue;
  }, [debouncedFetchGroupsUsers]);

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={({ handleSubmit: formHandleSubmit }) => (
        <form
          className="service-case-detail-form content-with-actions"
          onSubmit={formHandleSubmit}
        >
          <div className="content-container content-container--medium">
            <PrimaryWorkerSelectField
              options={options}
              onInputChange={handleInputChange}
            />
          </div>
          <div className="actions">
            <span className="action-item">
              <Button
                id="edit-primary-worker-cancel-btn"
                label="Cancel"
                onClick={closeModal}
                disabled={submitting}
              />
            </span>
            <span className="action-item">
              <Button
                id="edit-primary-worker-submit-btn"
                onClick={formHandleSubmit}
                primary
                disabled={submitting}
                label="Submit"
              />
            </span>
          </div>
        </form>
      )}
    />
  );
}

ServiceCasePrimaryWorkerForm.propTypes = {
  closeModal: PropTypes.func,
  serviceCase: PropTypes.shape({
    id: PropTypes.string.isRequired,
    program: PropTypes.object.isRequired,
    network: PropTypes.shape({
      id: PropTypes.string,
    }),
    primary_worker: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  fetchServiceCase: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  updateServiceCase: PropTypes.func.isRequired,
  contactId: PropTypes.string.isRequired,
  fetchProvidersUserCore: PropTypes.func.isRequired,
  fetchGroupsUsersFromProgram: PropTypes.func.isRequired,
  setDashboardRefetch: PropTypes.func.isRequired,
  groupsUsers: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        user: PropTypes.shape({
          active_in_current_group: PropTypes.bool,
        }).isRequired,
        employee: PropTypes.shape({
          id: PropTypes.string,
          full_name: PropTypes.string,
          user: PropTypes.shape({
            id: PropTypes.string,
          }),
        }).isRequired,
      }),
    ),
    isFetching: PropTypes.bool,
  }),
};

ServiceCasePrimaryWorkerForm.defaultProps = {
  closeModal: () => {},
  groupsUsers: { data: [] },
};

function mapStateToProps(state) {
  return {
    groupId: state.session.groupId,
    groupsUsers: state.groupsUsers,
  };
}

export default connect(
  mapStateToProps,
  {
    updateServiceCase,
    fetchProvidersUserCore,
    fetchGroupsUsersFromProgram,
    fetchServiceCase,
    setDashboardRefetch,
  },
)(ServiceCasePrimaryWorkerForm);
